"use client";

import { useEffect, useState } from "react";
import SelectField from "@/components/forms/fields/SelectField";
import type { FieldProps } from "@/components/forms/fields/shared";
import { locationOptions } from "@/lib/locations";
import { SUGGEST_LOCATION } from "./fields";

const SUGGEST_LABEL = "My area isn't listed: I'd like to suggest it";

/**
 * proposed_location: the published locations as a select, plus "My area isn't
 * listed". Its options come from the database, so they're loaded here rather
 * than declared with the field. A link from a location's card
 * (/become-a-das-centre?location=das-bury) chooses that location.
 */
export default function LocationSelect(props: FieldProps) {
  const { name, onChange } = props;
  const [options, setOptions] = useState<Record<string, string> | null>(null);

  useEffect(() => {
    let current = true;
    locationOptions().then((locations) => {
      if (!current) return;
      setOptions({
        ...Object.fromEntries(
          locations.map((location) => [location.slug, location.label]),
        ),
        [SUGGEST_LOCATION]: SUGGEST_LABEL,
      });

      const linked = new URLSearchParams(window.location.search).get(
        "location",
      );
      const known =
        linked === SUGGEST_LOCATION ||
        locations.some((location) => location.slug === linked);
      if (linked && known && name) onChange?.(name, linked);
    });
    return () => {
      current = false;
    };
    // Once, on load: the link's choice is a starting point, not a rule.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!props.field) return null;

  return (
    <SelectField
      {...props}
      field={{
        ...props.field,
        options: options ?? { [SUGGEST_LOCATION]: SUGGEST_LABEL },
        placeholder: options ? "Select a location" : "Loading locations…",
      }}
    />
  );
}
